import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  Menu, 
  ArrowUpRight,
  ArrowDownLeft,
  TrendingUp,
  TrendingDown,
  Gift,
  Brain,
  ChevronRight,
  Wallet,
  DollarSign,
  CreditCard,
  Lightbulb
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f04fecbe-9399-4471-a47d-7dd5f6d0f107/artifacts/x9r7wgb6_App%20Development.png";

export const DashboardHome = ({ onMenuClick }) => {
  const { user, api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [aiInsights, setAiInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalIncome: 0,
    totalSpend: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [txRes, aiRes] = await Promise.all([
        api.get('/transactions'),
        api.get('/ai/insights')
      ]);
      setTransactions(txRes.data.slice(0, 5));
      setAiInsights(aiRes.data);
      
      // Calculate stats from transactions
      const allTx = txRes.data;
      let income = 0;
      let spend = 0;
      
      allTx.forEach(tx => {
        if (['deposit', 'bonus'].includes(tx.type)) {
          income += tx.amount;
        } else if (['payment', 'transfer', 'airtime', 'electricity'].includes(tx.type)) {
          spend += tx.amount;
        }
      });
      
      setStats({ totalIncome: income, totalSpend: spend });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount, type) => {
    const isPositive = ['deposit', 'bonus'].includes(type);
    return `${isPositive ? '+' : '-'}R${Math.abs(amount).toFixed(2)}`;
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'deposit': return <ArrowDownLeft className="w-4 h-4 text-green-500" />;
      case 'bonus': return <Gift className="w-4 h-4 text-afriperx-gold" />;
      default: return <ArrowUpRight className="w-4 h-4 text-red-500" />;
    }
  };

  // Get AI recommendation
  const getAIRecommendation = () => {
    if (!aiInsights?.insights?.length) {
      return "Start making transactions to get personalized recommendations";
    }
    return aiInsights.insights[0]?.message || "Keep tracking your spending for better insights";
  };

  return (
    <div className="space-y-6" data-testid="dashboard-home">
      {/* Header */}
      <header className="flex items-center justify-between bg-gradient-to-r from-afriperx-green to-afriperx-green-dark rounded-2xl p-6 shadow-lg">
        <div className="flex items-center gap-4">
          <button 
            onClick={onMenuClick}
            className="lg:hidden text-white/80 hover:text-white"
            data-testid="menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div>
            <p className="text-white/80 text-sm">Welcome back,</p>
            <h1 className="font-syne text-2xl md:text-3xl font-bold text-white">{user?.name} {user?.surname}</h1>
          </div>
        </div>
        <div className="hidden md:block">
          <img src="https://customer-assets-agu9un31.emergentagent.net/job_c9a69f8c-f09a-4a22-b109-5dc5ad9f5a15/artifacts/qtk9sn35_MEDX-AI-Welcome-Poster.pptx%20%281%29.png" alt="AfriPerx" className="h-9 w-auto" />
        </div>
      </header>

      {/* Stats Tabs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Total Balance */}
        <Card className="stat-tab p-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-green-500/5 rounded-full -mr-10 -mt-10" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Total Balance</p>
            <p className="text-2xl font-bold text-gray-900">R{user?.main_balance?.toFixed(2) || '0.00'}</p>
          </div>
        </Card>

        {/* Total Monthly Spend */}
        <Card className="stat-tab p-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-red-500/5 rounded-full -mr-10 -mt-10" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-red-600" />
              </div>
            </div>
            <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Total Monthly Spend</p>
            <p className="text-2xl font-bold text-gray-900">R{stats.totalSpend.toFixed(2)}</p>
          </div>
        </Card>

        {/* Token Wallet Balance - Fixed at 2500 */}
        <Card className="stat-tab p-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-yellow-500/5 rounded-full -mr-10 -mt-10" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center">
                <Gift className="w-5 h-5 text-yellow-600" />
              </div>
            </div>
            <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Token Wallet Balance</p>
            <p className="text-2xl font-bold text-gray-900">{user?.loyalty_points?.toLocaleString() || '2,500'}</p>
          </div>
        </Card>

        {/* AI Recommendation */}
        <Card className="stat-tab p-5 relative overflow-hidden border-afriperx-gold/20">
          <div className="absolute top-0 right-0 w-20 h-20 bg-afriperx-green/5 rounded-full -mr-10 -mt-10" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-afriperx-gold" />
              </div>
            </div>
            <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">AI Recommendation</p>
            <p className="text-sm font-medium text-gray-700 line-clamp-2">{getAIRecommendation()}</p>
          </div>
        </Card>
      </div>

      {/* Account Balance Card */}
      <Card className="card-raoul p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-500 text-sm mb-1">Available Balance</p>
            <p className="text-4xl font-bold text-gray-900">R{user?.main_balance?.toFixed(2)}</p>
          </div>
          <div className="text-right">
            <p className="text-gray-500 text-sm mb-1">Savings</p>
            <p className="text-2xl font-bold text-gray-900">R{user?.savings_balance?.toFixed(2)}</p>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100 flex gap-3">
          <Button
            onClick={() => navigate('/dashboard/banking')}
            className="btn-primary flex-1"
            data-testid="go-to-banking-btn"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            View Banking
          </Button>
          <Button
            onClick={() => navigate('/dashboard/loyalty')}
            variant="outline"
            className="flex-1 border-gray-200 text-gray-700 hover:bg-gray-50"
            data-testid="go-to-rewards-btn"
          >
            <Gift className="w-4 h-4 mr-2" />
            Redeem Points
          </Button>
        </div>
      </Card>

      {/* AI Insights */}
      {aiInsights && (
        <Card className="card-raoul p-5" data-testid="ai-insights-card">
          <div className="flex items-center gap-2 mb-4">
            <Brain className="w-5 h-5 text-afriperx-gold" />
            <h3 className="font-syne text-lg font-bold text-gray-900">AI Financial Advisor</h3>
          </div>
          <div className="space-y-3">
            {aiInsights.insights?.slice(0, 3).map((insight, index) => (
              <div 
                key={index}
                className="ai-insight-card p-3 rounded-lg bg-gradient-to-r from-red-50 to-transparent"
              >
                <p className="text-gray-900 text-sm font-medium mb-1">{insight.title}</p>
                <p className="text-gray-500 text-xs">{insight.message}</p>
              </div>
            ))}
          </div>
          <Button
            onClick={() => navigate('/dashboard/banking')}
            variant="ghost"
            className="w-full mt-4 text-afriperx-gold hover:text-afriperx-gold-dark hover:bg-red-50"
            data-testid="view-all-insights-btn"
          >
            View All Insights
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </Card>
      )}

      {/* Recent Transactions */}
      <Card className="card-raoul p-5" data-testid="recent-transactions-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-syne text-lg font-bold text-gray-900">Recent Transactions</h3>
          <Button
            onClick={() => navigate('/dashboard/banking')}
            variant="ghost"
            size="sm"
            className="text-gray-500 hover:text-gray-900"
            data-testid="view-all-transactions-btn"
          >
            View All
          </Button>
        </div>
        
        {transactions.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No transactions yet</p>
        ) : (
          <div className="space-y-3">
            {transactions.map((tx) => (
              <div 
                key={tx.id}
                className="transaction-item flex items-center justify-between p-3 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    {getTransactionIcon(tx.type)}
                  </div>
                  <div>
                    <p className="text-gray-900 text-sm font-medium">{tx.description}</p>
                    <p className="text-gray-400 text-xs">
                      {new Date(tx.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <p className={`font-medium ${
                  ['deposit', 'bonus'].includes(tx.type) ? 'text-green-600' : 'text-red-600'
                }`}>
                  {formatAmount(tx.amount, tx.type)}
                </p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};
